import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchHolidayThemeCurrent, holidayThemeCurrentKey } from '../data/holidayThemes';
import {
  holidayWindowOpenAt,
  isHolidayThemeKey,
  nextHolidayBoundary,
  openHolidayWindow,
  type HolidayThemeKey,
  type HolidayWindow,
} from '../domain/holidayThemes';
import { HOLIDAY_PREVIEW_EVENT, readHolidayPreview, readHolidayThemeCache, writeHolidayThemeCache } from './shellTheme';

// setTimeout overflows past ~24.8 days; a long wait simply re-arms.
const MAX_TIMER_MS = 6 * 60 * 60 * 1000;

/**
 * The scheduled holiday window open right now, for every screen (docs/holiday-themes.md). Until the first
 * answer arrives, the cached window keeps the first paint's holiday. A timer flips the theme on at the
 * window's start and off at its end without a reload, using the server's clock.
 */
export function useScheduledHolidayTheme(): { scheduled: HolidayWindow | null; preview: HolidayThemeKey | null } {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: holidayThemeCurrentKey,
    queryFn: fetchHolidayThemeCurrent,
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  // A failed background refetch keeps the last answer (React Query v5 keeps data in the error state).
  const answered = query.data !== undefined;
  const state = answered ? query.data : null;
  const [cached] = React.useState(readHolidayThemeCache);
  const [tick, setTick] = React.useState(0);
  const [preview, setPreview] = React.useState<HolidayThemeKey | null>(readHolidayPreview);

  // The device clock may be off; measure against the server's clock from the last answer.
  const offsetMs = state ? Date.parse(state.serverNow) - query.dataUpdatedAt : 0;

  const scheduled = React.useMemo(() => {
    void tick;
    const now = Date.now() + offsetMs;
    if (answered) return openHolidayWindow(state, now);
    return holidayWindowOpenAt(cached, now) ? cached : null;
  }, [answered, cached, offsetMs, state, tick]);

  React.useEffect(() => {
    if (!answered) return;
    // Keep the window that is open, or else the next one, so a later visit paints it before fetching.
    writeHolidayThemeCache(state ? (state.active ?? state.next) : null);
  }, [answered, state]);

  React.useEffect(() => {
    const now = Date.now() + offsetMs;
    const boundary = answered
      ? nextHolidayBoundary(state, now)
      // The cache may hold an upcoming window, so arm both its start and its end.
      : cached ? nextHolidayBoundary({ active: null, next: cached, serverNow: '' }, now) : null;
    if (boundary === null) return undefined;
    const wait = boundary - now;
    const timer = window.setTimeout(() => {
      setTick((value) => value + 1);
      // Past a boundary, ask again so the following window is known.
      if (wait <= MAX_TIMER_MS) void queryClient.invalidateQueries({ queryKey: holidayThemeCurrentKey });
    }, Math.min(Math.max(wait, 0) + 250, MAX_TIMER_MS));
    return () => window.clearTimeout(timer);
  }, [answered, cached, offsetMs, queryClient, state, tick]);

  React.useEffect(() => {
    const onPreview = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      setPreview(isHolidayThemeKey(detail) ? detail : null);
    };
    window.addEventListener(HOLIDAY_PREVIEW_EVENT, onPreview);
    return () => window.removeEventListener(HOLIDAY_PREVIEW_EVENT, onPreview);
  }, []);

  return React.useMemo(() => ({ scheduled, preview }), [preview, scheduled]);
}
